import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast, { Toaster } from 'react-hot-toast';

interface QuoteFormProps {
  quote?: any; // Si fourni → mode modification
  onBack: () => void;
}

interface Client {
  id: number;
  raison_sociale: string;
  email: string | null;
  code: string;
}

interface Product {
  id: number;
  designation: string; // ou name selon ton API
  reference: string;   // ou sku
  salePrice: number; // prix réel depuis l'API
  vatSale: number;
}

interface Commercial {
  id: number;
  name: string;
}

export default function QuoteForm({ quote, onBack }: QuoteFormProps) {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [commercials, setCommercials] = useState<Commercial[]>([]);

  const isEditMode = !!quote;

  const [formData, setFormData] = useState({
    client_id: null as number | null,
    customerName: '',
    customerEmail: '',
    salesperson: '',
    date: '',
    validUntil: '',
    notes: '',
    remise_globale: 0,
    items: [] as any[]
  });

  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [productDiscount, setProductDiscount] = useState(0);

  // Chargement initial des données (clients, produits, commerciaux)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, productsRes, commercialsRes] = await Promise.all([
          getResources('clients'),
          getResources('produits'),
          getResources('devis-bcc/commerciaux')
        ]);

        setClients(clientsRes.data.data || []);
        setProducts(productsRes.data || []);
        setCommercials(commercialsRes.data || []);
      } catch (err) {
        toast.error('Erreur lors du chargement des données');
      }
    };
    fetchData();
  }, []);

  // Pré-remplissage en mode édition
  useEffect(() => {
    if (isEditMode && quote) {
      setFormData({
        client_id: quote.client?.id || null,
        customerName: quote.client?.raison_sociale || '',
        customerEmail: quote.client?.email || '',
        salesperson: quote.commercial.id || '',
        date: quote.date_devis,
        validUntil: quote.date_validite,
        notes: quote.notes_client || '',
        remise_globale: Number(quote.remise_globale ?? 0),
        items: (quote.lignes || []).map((ligne: any) => ({
          id: ligne.id || Date.now(),
          produit_id: ligne.produit_id,
          productName: ligne.designation || ligne.produit?.designation || 'Produit',
          sku: ligne.reference || ligne.produit?.reference || '',
          unitPrice: Number(ligne.prix_unitaire_ht || 0),
          quantity: ligne.quantite,
          discount: Number(ligne.remise_ligne || 0),
          discountAmount: 0, // recalculé plus bas
          totalPrice: 0,    // recalculé plus bas
          vatRate: Number(ligne.tva_percent ?? 0)
        }))
      });
    } else {
      // Mode création
      setFormData({
        client_id: null,
        customerName: '',
        customerEmail: '',
        salesperson: '',
        remise_globale: 0,
        date: new Date().toISOString().split('T')[0],
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: '',
        items: []
      });
    }
  }, [quote, isEditMode]);

  // Recalcul des totaux de ligne après chargement
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        const lineTotal = item.unitPrice * item.quantity;
        const discountAmount = lineTotal * (item.discount / 100);
        const totalPrice = lineTotal - discountAmount;
        return { ...item, discountAmount, totalPrice };
      })
    }));
  }, []);

  const addProductToQuote = () => {
    if (!selectedProduct) return;

    const prixUnitaire = Number(selectedProduct.salePrice || 0);
    const lineTotal = prixUnitaire * productQuantity;
    const discountAmount = lineTotal * (productDiscount / 100);
    const lineTotalWithDiscount = lineTotal - discountAmount;

    const newItem = {
      id: Date.now(),
      produit_id: selectedProduct.id,
      productName: selectedProduct.designation || selectedProduct.name || 'Produit',
      sku: selectedProduct.reference || selectedProduct.sku || '',
      unitPrice: prixUnitaire,
      quantity: productQuantity,
      discount: productDiscount,
      discountAmount,
      totalPrice: lineTotalWithDiscount,
      vatRate: Number(selectedProduct.vatSale ?? 0)
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setShowProductModal(false);
    setSelectedProduct(null);
    setProductQuantity(1);
    setProductDiscount(0);
  };

  const removeItem = (itemId: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const updateItemQuantity = (itemId: number, quantity: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const lineTotal = item.unitPrice * quantity;
          const discountAmount = lineTotal * (item.discount / 100);
          const totalPrice = lineTotal - discountAmount;
          return { ...item, quantity, discountAmount, totalPrice };
        }
        return item;
      })
    }));
  };

  const updateItemDiscount = (itemId: number, discount: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const lineTotal = item.unitPrice * item.quantity;
          const discountAmount = lineTotal * (discount / 100);
          const totalPrice = lineTotal - discountAmount;
          return { ...item, discount, discountAmount, totalPrice };
        }
        return item;
      })
    }));
  };

const calculateTotals = () => {
  let subtotal = 0;
  let totalDiscountLines = 0;
  let totalHT = 0;
  let totalVAT = 0;

  formData.items.forEach(item => {
    const qty = Number(item.quantity || 0);
    const price = Number(item.unitPrice || 0);
    const discountPct = Number(item.discount || 0);
    const vatPct = Number(item.vatRate || 0);

    const lineHT = qty * price;
    const discount = lineHT * (discountPct / 100);
    const netHT = lineHT - discount;
    const vat = netHT * (vatPct / 100);

    subtotal += lineHT;
    totalDiscountLines += discount;
    totalHT += netHT;
    totalVAT += vat;
  });

  const remiseGlobale = Math.min(
    Number(formData.remise_globale || 0),
    totalHT
  );

  // ventilation remise globale
  if (remiseGlobale > 0 && totalHT > 0) {
    const ratio = remiseGlobale / totalHT;
    totalHT = totalHT - remiseGlobale;
    totalVAT = totalVAT * (1 - ratio);
  }

  const totalTTC = totalHT + totalVAT;

  return {
    subtotal,
    totalDiscountLines,
    remiseGlobale,
    totalDiscount: totalDiscountLines + remiseGlobale,
    totalHT,
    totalVAT,
    totalTTC,
  };
};



  const totals = calculateTotals();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Ajoutez au moins un produit');
      return;
    }
    if (new Date(formData.validUntil) < new Date(formData.date)) {
      toast.error('La date de validité doit être postérieure à la date du devis');
      return;
    }

    setLoading(true);

    const payload = {
      client_id: formData.client_id,
      commercial_id: formData.salesperson || null,
      date_devis: formData.date,
      date_validite: formData.validUntil,
      remise_globale: formData.remise_globale, // ✅ AJOUT
      notes_client: formData.notes || null,
      lignes: formData.items.map(item => ({
        produit_id: item.produit_id,
        quantite: item.quantity,
        prix_unitaire_ht: item.unitPrice,
        remise_ligne: item.discount,
        tva_percent: Number(item.vatRate ?? 0), // 🔒 IMPORTANT
      }))
    };

    try {
      if (isEditMode && quote?.id) {
        await updateResource(`devis-bcc`,quote.id, payload);
        toast.success('Devis modifié avec succès !');
      } else {
        await createResource('devis-bcc', payload);
        toast.success('Devis créé avec succès !');
      }
      onBack();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erreur lors de l\'enregistrement';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    
    <div className="space-y-6">
      
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900">
          <i className="ri-arrow-left-line mr-2"></i>
          Retour à la liste
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Informations Générales</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client *
              </label>
              <select
                value={formData.client_id || ''}
                onChange={(e) => {
                  const clientId = e.target.value ? parseInt(e.target.value) : null;
                  const client = clients.find(c => c.id === clientId);
                  setFormData({
                    ...formData,
                    client_id: clientId,
                    customerName: client ? client.raison_sociale : '',
                    customerEmail: client?.email || ''
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                required
              >
                <option value="">-- Sélectionner un client --</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.raison_sociale} ({client.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email du client
              </label>
              <input
                type="email"
                value={formData.customerEmail}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commercial
              </label>
              <select
                value={formData.salesperson}
                onChange={(e) => setFormData({ ...formData, salesperson: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              >
                <option value="">Sélectionner un commercial</option>
                {commercials.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date du devis
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valide jusqu'au *
              </label>
              <input
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes et conditions
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Conditions de paiement, délais de livraison, etc."
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Remise globale (GNF)
          </label>
          <input
            type="number"
            min="0"
            value={formData.remise_globale}
            onChange={(e) =>
              setFormData({
                ...formData,
                remise_globale: Number(e.target.value || 0),
              })
            }
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        {/* Quote Items */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Lignes du Devis</h3>
            <button
              type="button"
              onClick={() => setShowProductModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Ajouter un produit
            </button>
          </div>

          {formData.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Produit</th>
                    <th className="text-right py-2 text-sm font-medium text-gray-700">Prix Unit.</th>
                    <th className="text-center py-2 text-sm font-medium text-gray-700">Qté</th>
                    <th className="text-center py-2 text-sm font-medium text-gray-700">Remise %</th>
                    <th className="text-right py-2 text-sm font-medium text-gray-700">Total HT</th>
                    <th className="text-center py-2 text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item: any) => (
                    <tr key={item.id} className="border-b">
                      <td className="py-3">
                        <div>
                          <div className="font-medium">{item.productName}</div>
                          <div className="text-sm text-gray-500">{item.sku}</div>
                        </div>
                      </td>
                      <td className="py-3 text-right">{Number(item.unitPrice).toFixed(2)} GNF</td>
                      <td className="py-3 text-center">
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItemQuantity(item.id, parseInt(e.target.value) || 1)}
                          className="w-16 px-2 py-1 text-center border border-gray-300 rounded"
                        />
                      </td>
                      <td className="py-3 text-center">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={item.discount}
                          onChange={(e) => updateItemDiscount(item.id, parseFloat(e.target.value) || 0)}
                          className="w-16 px-2 py-1 text-center border border-gray-300 rounded"
                        />
                      </td>
                      <td className="py-3 text-right font-medium">{Number(item.totalPrice).toFixed(2)} GNF</td>
                      <td className="py-3 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="mt-6 flex justify-end">
                <div className="w-80">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Sous-total HT :</span>
                      <span>{totals.subtotal.toFixed(2)} GNF</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>Remise totale :</span>
                      <span>-{totals.totalDiscount.toFixed(2)} GNF</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span>Total HT :</span>
                      <span className="font-medium">{totals.totalHT.toFixed(2)} GNF</span>
                    </div>
                    <div className="flex justify-between">
                      <span>TVA (18%) :</span>
                      <span>{totals.totalVAT.toFixed(2)} GNF</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 text-lg font-bold">
                      <span>Total TTC :</span>
                      <span>{totals.totalTTC.toFixed(2)} GNF</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <i className="ri-shopping-cart-line text-3xl mb-2"></i>
              <p>Aucun produit ajouté</p>
            </div>
          )}
        </div>

        {/* Product Selection Modal */}
        {showProductModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-y-auto">
              <h3 className="text-lg font-medium mb-4">Sélectionner un produit</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Produit</label>
                   <select
                    value={selectedProduct?.id || ''}
                    onChange={(e) => {
                      const product = products.find(p => p.id === e.target.value); // <-- pas parseInt
                      setSelectedProduct(product || null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"

                  >
                    <option value="">Choisir un produit</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {product.salePrice.toFixed(2)} GNF
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quantité</label>
                  <input
                    type="number"
                    min="1"
                    value={productQuantity}
                    onChange={(e) => setProductQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remise (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={productDiscount}
                    onChange={(e) => setProductDiscount(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowProductModal(false);
                    setSelectedProduct(null);
                    setProductQuantity(1);
                    setProductDiscount(0);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={addProductToQuote}
                  disabled={!selectedProduct}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap disabled:opacity-50"
          >
            {loading ? 'Enregistrement...' : isEditMode ? 'Modifier le devis' : 'Créer le devis'}
          </button>
        </div>
      </form>
    </div>
  );
}